import { useDeviceType } from "@dash/mq";
import { HeaderBar, Page, SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { useProfiles } from "@megarax/features";
import { useMemo } from "react";
import { useParams } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";
import { getMonths } from "@megaron/serializers";

import { useRegionProfitMetricsFilters } from "../useRegionProfitMetricsFilters";
import { RegionDetails } from "./RegionDetails";
import { RegionOrders } from "./RegionOrders";
import { RegionRealizationSection } from "./RegionRealizationSection";

export const RegionPage = () => {
  const { region } = useParams<{ region: string }>();

  const { isDesktop, isMobile } = useDeviceType();

  const { filters, setFilters } = useRegionProfitMetricsFilters();

  const ordersQuery = useClientManager("docs")
    .searchOrders()
    .useQuery({
      region: region ? [region] : undefined,
      limit: region ? 10 : 1000,
      month: getMonths(filters.startDate, filters.endDate),
    });

  const regionQuery = useClientManager("crm").regionsQuery().useQuery({ regionName: region }, { enabled: !!region });

  const legacyProfileQuery = useClientManager("auth")
    .userProfilesQuery()
    .useQuery(
      { emails: [regionQuery.data?.items[0]?.ownerEmail ?? ""] },
      { enabled: !!regionQuery.data?.items[0]?.ownerEmail },
    );

  const legacyUserId = useMemo(() => legacyProfileQuery.data?.items[0]?.legacyUserId, [legacyProfileQuery.data]);

  const { profile } = useProfiles();

  const salespersonProfile = useMemo(
    () => (regionQuery.data?.items[0] ? profile(regionQuery.data.items[0].ownerEmail) ?? undefined : undefined),
    [regionQuery.data, profile],
  );

  if (!region) {
    return <div>Error: Region not provided.</div>;
  }

  return (
    <>
      <HeaderBar header={region} />
      <Page
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginBottom: "2rem",
          padding: isMobile ? "0 1rem" : "0 2rem",
          marginTop: "83px",
        }}
      >
        <div
          css={{
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            gap: "2rem",
            width: "100%",
          }}
        >
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: "2rem",
              width: "100%",
              minWidth: "17rem",
            }}
          >
            <QuerySkeleton query={regionQuery}>
              {(regionData) => (
                <RegionDetails
                  profile={salespersonProfile}
                  legacyUserId={legacyUserId}
                  region={regionData.items[0]}
                  customerCount={regionData.items[0].customerCount}
                  regionQueryKey={regionQuery.key}
                />
              )}
            </QuerySkeleton>
            <RegionRealizationSection
              startDate={filters.startDate}
              endDate={filters.endDate}
              region={region}
              onDatesChange={(startDate, endDate) => setFilters({ startDate, endDate })}
            />
          </div>

          <div css={{ display: "flex", flexDirection: "column", gap: "1rem", minWidth: "400px" }}>
            <SectionHeader isHr css={{ marginTop: 0 }}>
              Zamówienia
            </SectionHeader>
            <QuerySkeleton query={ordersQuery} height="158px" width="100%">
              {(orders) => (
                <RegionOrders
                  orders={region ? orders.items : orders.items.filter((order) => !order.region)}
                  region={region}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                />
              )}
            </QuerySkeleton>
          </div>
        </div>
      </Page>
    </>
  );
};
